/**
 * <signup-popup> (sections/signup-popup.liquid) decides WHEN a popup opens.
 *
 *   engaged  from the visitor's second page view, after the delay or once
 *            the page is scrolled past the depth, whichever comes first
 *   exit     the cursor leaving through the top of the window (desktop);
 *            phones have no such gesture and fall back to "engaged"
 *   soon     after the delay, first page included
 *
 * Memory, all wrapped in try/catch because blocked storage throws on access:
 *   localStorage  waimers:popup:<section>  when this one was last shown
 *   localStorage  waimers:subscribed       set on a successful signup; ends
 *                                          every popup for good
 *   sessionStorage waimers:pages           page views this visit
 *   sessionStorage waimers:popup-seen      which popup this visit showed;
 *                                          the others stay quiet
 *   window flag                            one popup per page view
 *
 * Timers use setTimeout and a plain scroll listener, never rAF: rAF is
 * paused in background tabs and the popup would open the moment a visitor
 * came back to the tab, which reads as an ambush.
 */
if (!customElements.get('signup-popup')) {
  var DAY = 86400000;
  var store = {
    get: function (area, key) { try { return window[area].getItem(key); } catch (e) { return null; } },
    set: function (area, key, value) { try { window[area].setItem(key, value); } catch (e) { /* blocked */ } }
  };

  (function countPage() {
    if (window.__waimersPageCounted) return;
    window.__waimersPageCounted = true;
    var n = parseInt(store.get('sessionStorage', 'waimers:pages') || '0', 10) + 1;
    store.set('sessionStorage', 'waimers:pages', String(n));
  })();

  class SignupPopup extends HTMLElement {
    connectedCallback() {
      this.card = this.querySelector('.signup-popup__card');
      this.key = 'waimers:popup:' + (this.dataset.sectionId || '');
      this.isOpen = false;
      this.onKey = this.onKey.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onMouseOut = this.onMouseOut.bind(this);

      this.querySelectorAll('[data-popup-close]').forEach((el) => el.addEventListener('click', () => this.close()));
      var copy = this.querySelector('[data-copy-code]');
      if (copy) copy.addEventListener('click', () => this.copyCode(copy));

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:section:select', (e) => { if (e.detail.sectionId === this.dataset.sectionId) this.open(true); });
        document.addEventListener('shopify:section:deselect', (e) => { if (e.detail.sectionId === this.dataset.sectionId) this.close(true); });
        return;
      }

      var message = this.querySelector('[data-popup-message]');
      if (message) {
        if (message.dataset.popupMessage === 'success') store.set('localStorage', 'waimers:subscribed', String(Date.now()));
        /* Every popup on the page sees the post; the one whose form it was
           (named in the hash) answers, else the first. */
        var mine = window.location.hash === '#' + (this.querySelector('form') || {}).id;
        if (window.__waimersPopupShown && !mine) return;
        this.open(true);
        return;
      }

      if (this.suppressed()) return;
      this.arm();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      window.removeEventListener('scroll', this.onScroll);
      document.removeEventListener('mouseout', this.onMouseOut);
      document.removeEventListener('keydown', this.onKey);
    }

    suppressed() {
      if (store.get('localStorage', 'waimers:subscribed')) return true;
      var seen = store.get('sessionStorage', 'waimers:popup-seen');
      if (seen && seen !== this.key) return true;
      var last = parseInt(store.get('localStorage', this.key) || '0', 10);
      var days = Number(this.dataset.repeatDays) || 14;
      return last && Date.now() - last < days * DAY;
    }

    isPhone() {
      return !window.matchMedia('(pointer: fine)').matches || window.matchMedia('(max-width: 749px)').matches;
    }

    arm() {
      var trigger = this.dataset.trigger || 'engaged';
      var delay = Number(this.isPhone() ? this.dataset.delayMobile : this.dataset.delay) || 20;

      if (trigger === 'exit' && !this.isPhone()) {
        document.addEventListener('mouseout', this.onMouseOut);
        return;
      }
      if (trigger !== 'soon') {
        var pages = parseInt(store.get('sessionStorage', 'waimers:pages') || '1', 10);
        if (pages < 2) return;
        this.depth = Math.min(Math.max(Number(this.dataset.scroll) || 50, 10), 100);
        window.addEventListener('scroll', this.onScroll, { passive: true });
      }
      this.timer = setTimeout(() => this.open(), delay * 1000);
    }

    onScroll() {
      var range = document.documentElement.scrollHeight - window.innerHeight;
      if (range > 0 && (window.scrollY / range) * 100 >= this.depth) this.open();
    }

    onMouseOut(e) {
      if (e.relatedTarget || e.clientY > 0) return;
      this.open();
    }

    open(force) {
      if (this.isOpen) return;
      if (!force) {
        if (window.__waimersPopupShown || document.hidden || this.suppressed()) return;
        /* Wait for the cookie banner to be answered rather than stack on it. */
        var banner = document.querySelector('.cookie-banner');
        if (banner && !banner.hidden) { clearTimeout(this.timer); this.timer = setTimeout(() => this.open(), 4000); return; }
      }
      window.__waimersPopupShown = true;
      clearTimeout(this.timer);
      window.removeEventListener('scroll', this.onScroll);
      document.removeEventListener('mouseout', this.onMouseOut);
      if (!(window.Shopify && window.Shopify.designMode)) {
        store.set('localStorage', this.key, String(Date.now()));
        store.set('sessionStorage', 'waimers:popup-seen', this.key);
      }

      this.isOpen = true;
      this.lastFocus = document.activeElement;
      this.hidden = false;
      /* A timeout, not rAF, to let the hidden -> shown frame paint first. */
      setTimeout(() => this.classList.add('is-open'), 30);
      document.addEventListener('keydown', this.onKey);
      if (this.dataset.style === 'figure') {
        document.documentElement.classList.add('signup-popup-lock');
        setTimeout(() => { var input = this.querySelector('input[type="email"]'); (input || this.card).focus({ preventScroll: true }); }, 450);
      }
    }

    close(fromEditor) {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.classList.remove('is-open');
      document.removeEventListener('keydown', this.onKey);
      document.documentElement.classList.remove('signup-popup-lock');
      setTimeout(() => { if (!this.isOpen) this.hidden = true; }, 450);
      if (!fromEditor && this.lastFocus && this.dataset.style === 'figure') this.lastFocus.focus({ preventScroll: true });
    }

    onKey(e) {
      if (e.key === 'Escape') this.close();
    }

    copyCode(btn) {
      var code = (this.querySelector('[data-code]') || {}).textContent || '';
      var done = function () { btn.textContent = 'Copied'; };
      try { navigator.clipboard.writeText(code.trim()).then(done, done); } catch (e) { done(); }
    }
  }

  customElements.define('signup-popup', SignupPopup);
}
